import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '../../../services/toast.service';
import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';
import { RestaurantService } from '../../../services/restaurant.service';
import {
  CHANNEL_LABELS,
  CategoryPreference,
  NOTIFICATION_CHANNELS,
  NotificationChannel,
  NotificationPreferences,
  NotificationPreferencesPatch,
  SummaryCadence,
  categoryIcon,
} from '../../../models/notification';

/** One row of the muted-restaurants card. */
interface MutedRestaurant {
  id: string;
  name: string;
  isCurrent: boolean;
}

/** Timezones the quiet-hours picker offers, plus whatever the document holds. */
const TIMEZONES: { id: string; label: string }[] = [
  { id: 'America/Los_Angeles', label: 'Pacific Time' },
  { id: 'America/Denver', label: 'Mountain Time' },
  { id: 'America/Phoenix', label: 'Arizona' },
  { id: 'America/Chicago', label: 'Central Time' },
  { id: 'America/New_York', label: 'Eastern Time' },
  { id: 'America/Toronto', label: 'Toronto' },
  { id: 'America/Vancouver', label: 'Vancouver' },
];

/**
 * Settings → Notifications → Preferences. Figma "Notification Preferences"
 * (Admin-Dashboard 329:4182).
 *
 * The controls edit a draft; Save sends every changed field in one PATCH and
 * Cancel puts the draft back — the same grammar as Reservation settings and
 * the dashboard mobile app, rather than the design's per-toggle auto-save.
 */
@Component({
  selector: 'app-notification-preferences',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatTooltipModule],
  templateUrl: './notification-preferences.component.html',
  styleUrl: './notification-preferences.component.css',
})
export class NotificationPreferencesComponent implements OnInit {
  private readonly notifications = inject(NotificationService);
  private readonly auth = inject(AuthService);
  private readonly restaurants = inject(RestaurantService);
  private readonly toast = inject(ToastService);

  readonly channels = NOTIFICATION_CHANNELS;
  readonly channelLabels = CHANNEL_LABELS;
  readonly timezones = TIMEZONES;
  readonly cadences: { value: SummaryCadence; label: string }[] = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'off', label: 'Off' },
  ];

  readonly lockedMessage = 'Always on for billing and compliance';
  static readonly savedTitle = "You're all set";
  static readonly savedMessage =
    'Your notification preferences are saved. New alerts follow them from here on.';
  static readonly failedTitle = "Couldn't save. Try again.";

  loading = true;
  loadError = false;
  saving = false;
  advancedOpen = false;

  /** What the service last handed back, and the copy the page edits. */
  private savedDoc: NotificationPreferences | null = null;
  draft: NotificationPreferences | null = null;

  /** Contact lines under the Email and SMS channel rows. */
  email = '';
  phone = '';
  mutedRestaurants: MutedRestaurant[] = [];

  ngOnInit(): void {
    this.load();
    this.auth.getCurrentUser().subscribe({
      next: (res: any) => {
        const user = res?.data ?? res;
        this.email = (user?.email ?? '').trim();
        this.phone = (user?.phone_number ?? user?.phoneNumber ?? '').trim();
      },
      error: () => undefined,
    });
    this.restaurants.currentRestaurantDetails$.subscribe((restaurant) => {
      if (restaurant) {
        this.currentRestaurant = {
          id: restaurant.restaurant_id ?? restaurant.restaurantId ?? '',
          name: restaurant.restaurant_name ?? restaurant.restaurantName ?? 'This restaurant',
        };
        this.buildMutedRows();
      }
    });
  }

  private currentRestaurant: { id: string; name: string } | null = null;

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.notifications.getPreferences().subscribe({
      next: (doc) => {
        this.savedDoc = doc;
        this.draft = clone(doc);
        this.buildMutedRows();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  // ------------------------------------------------------------------ state

  get dirty(): boolean {
    return (
      !!this.draft &&
      !!this.savedDoc &&
      JSON.stringify(this.draft) !== JSON.stringify(this.savedDoc)
    );
  }

  get canSave(): boolean {
    return this.dirty && !this.saving;
  }

  /** The helper under the master switch changes with it. */
  get masterSubtitle(): string {
    return this.draft?.enabled
      ? 'Control all notification preferences'
      : 'Turn on to manage channels and categories.';
  }

  get emailSubtitle(): string {
    return this.email ? `Sent to ${this.email}` : 'No email on this account';
  }

  get smsSubtitle(): string {
    return this.phone ? `Text to ${formatPhone(this.phone)}` : 'Add a phone number in Profile';
  }

  /** Figma "Advanced" preview line. */
  get advancedSummary(): string {
    const t = this.draft?.thresholds;
    const rating = `Reviews ≤ ${t?.low_rating ?? 2} stars`;
    const amount =
      t?.large_order_amount == null
        ? 'Off'
        : `$${Number(t.large_order_amount).toLocaleString('en-US')}`;
    return `${rating} · Large order amount: ${amount}`;
  }

  channelOn(channel: NotificationChannel): boolean {
    return this.draft?.channels?.[channel] ?? true;
  }

  categoryIconFor(category: CategoryPreference): string {
    return categoryIcon(category.key);
  }

  isAllowed(category: CategoryPreference, channel: NotificationChannel): boolean {
    return (category.allowed ?? []).includes(channel);
  }

  isLocked(category: CategoryPreference, channel: NotificationChannel): boolean {
    return (category.locked ?? []).includes(channel);
  }

  cellValue(category: CategoryPreference, channel: NotificationChannel): boolean {
    return !!category[channel];
  }

  /** A cell the reader may move: allowed, not locked, and its channel is on. */
  cellEditable(category: CategoryPreference, channel: NotificationChannel): boolean {
    return (
      this.isAllowed(category, channel) &&
      !this.isLocked(category, channel) &&
      this.channelOn(channel) &&
      !!this.draft?.enabled &&
      !this.saving
    );
  }

  // ------------------------------------------------------------------ edits

  setMaster(on: boolean): void {
    if (this.draft) this.draft.enabled = on;
  }

  setChannel(channel: NotificationChannel, on: boolean): void {
    if (!this.draft) return;
    this.draft.channels = { ...this.draft.channels, [channel]: on };
  }

  setCell(category: CategoryPreference, channel: NotificationChannel, on: boolean): void {
    category[channel] = on;
  }

  setCadence(cadence: SummaryCadence): void {
    if (this.draft) this.draft.summary = { ...this.draft.summary, cadence };
  }

  setLowRating(delta: number): void {
    if (!this.draft) return;
    const next = clamp((this.draft.thresholds.low_rating ?? 2) + delta, 1, 5);
    this.draft.thresholds = { ...this.draft.thresholds, low_rating: next };
  }

  setLargeOrderAmount(raw: string): void {
    if (!this.draft) return;
    const value = Number(String(raw).replace(/[^0-9.]/g, ''));
    this.draft.thresholds = {
      ...this.draft.thresholds,
      large_order_amount: raw === '' || Number.isNaN(value) || value <= 0 ? null : value,
    };
  }

  clearLargeOrderAmount(): void {
    if (!this.draft) return;
    this.draft.thresholds = { ...this.draft.thresholds, large_order_amount: null };
  }

  /** On = this location still notifies; off = muted. */
  setRestaurantMuted(id: string, muted: boolean): void {
    if (!this.draft) return;
    const current = this.draft.muted_restaurants ?? [];
    this.draft.muted_restaurants = muted
      ? Array.from(new Set([...current, id]))
      : current.filter((r) => r !== id);
  }

  restaurantNotifying(id: string): boolean {
    return !(this.draft?.muted_restaurants ?? []).includes(id);
  }

  // ------------------------------------------------------------------- save

  cancel(): void {
    if (!this.savedDoc || this.saving) return;
    this.draft = clone(this.savedDoc);
  }

  save(): void {
    if (!this.draft || !this.savedDoc || this.saving) return;
    const body = NotificationPreferencesComponent.patchBody(this.savedDoc, this.draft);
    if (Object.keys(body).length === 0) return;

    this.saving = true;
    this.notifications.updatePreferences(body).subscribe({
      next: (doc) => this.commit(doc),
      error: (err: HttpErrorResponse) => {
        if (err.status === 409) {
          this.retryOnFresh(body);
          return;
        }
        this.saving = false;
        this.toast.error(
          reason(err) ?? 'Nothing was changed.',
          NotificationPreferencesComponent.failedTitle,
        );
      },
    });
  }

  /** Concurrent edit: take the other client's version, then try once more. */
  private retryOnFresh(body: NotificationPreferencesPatch): void {
    this.notifications.getPreferences().subscribe({
      next: () =>
        this.notifications.updatePreferences(body).subscribe({
          next: (doc) => this.commit(doc),
          error: (err: HttpErrorResponse) => {
            this.saving = false;
            this.toast.error(
              reason(err) ?? 'Nothing was changed.',
              NotificationPreferencesComponent.failedTitle,
            );
          },
        }),
      error: () => {
        this.saving = false;
        this.toast.error('Nothing was changed.', NotificationPreferencesComponent.failedTitle);
      },
    });
  }

  private commit(doc: NotificationPreferences): void {
    this.savedDoc = doc;
    this.draft = clone(doc);
    this.buildMutedRows();
    this.saving = false;
    this.toast.success(
      NotificationPreferencesComponent.savedMessage,
      NotificationPreferencesComponent.savedTitle,
    );
  }

  /**
   * The rows the muted card draws: the current restaurant first, then any
   * other location the document already mutes. One restaurant means nothing to
   * mute but yourself, so the card is left out entirely.
   */
  private buildMutedRows(): void {
    const rows: MutedRestaurant[] = [];
    const current = this.currentRestaurant;
    if (current?.id) {
      rows.push({ id: current.id, name: current.name, isCurrent: true });
    }
    for (const id of this.draft?.muted_restaurants ?? []) {
      if (!rows.some((r) => r.id === id)) {
        rows.push({ id, name: id, isCurrent: false });
      }
    }
    this.mutedRestaurants = rows.length > 1 ? rows : [];
  }

  /**
   * The partial-update body for what changed between `saved` and `draft`, in
   * the shape the PATCH endpoint reads.
   *
   * Locked cells and channels a category may not use never appear: the page
   * cannot change them, and sending them back would be refused.
   */
  static patchBody(
    saved: NotificationPreferences,
    draft: NotificationPreferences,
  ): NotificationPreferencesPatch {
    const body: NotificationPreferencesPatch = {};
    if (draft.enabled !== saved.enabled) body.enabled = draft.enabled;

    for (const channel of NOTIFICATION_CHANNELS) {
      const block: Partial<{ enabled: boolean; categories: Record<string, boolean> }> = {};
      const before = saved.channels?.[channel] ?? true;
      const after = draft.channels?.[channel] ?? true;
      if (before !== after) block.enabled = after;

      const categories: Record<string, boolean> = {};
      for (const category of draft.categories ?? []) {
        const previous = (saved.categories ?? []).find((c) => c.key === category.key);
        if (!previous) continue;
        if (!(category.allowed ?? []).includes(channel)) continue;
        if ((category.locked ?? []).includes(channel)) continue;
        if (!!category[channel] !== !!previous[channel]) {
          categories[category.key] = !!category[channel];
        }
      }
      if (Object.keys(categories).length > 0) block.categories = categories;
      if (Object.keys(block).length > 0) body[channel] = block;
    }

    const q = draft.quiet_hours;
    const q0 = saved.quiet_hours;
    const quiet: NotificationPreferencesPatch['quiet_hours'] = {};
    if (q.enabled !== q0.enabled) quiet.enabled = q.enabled;
    if (q.start !== q0.start) quiet.start = q.start;
    if (q.end !== q0.end) quiet.end = q.end;
    if (q.timezone !== q0.timezone) quiet.timezone = q.timezone;
    if (Object.keys(quiet).length > 0) body.quiet_hours = quiet;

    const summary: NotificationPreferencesPatch['summary'] = {};
    if (draft.summary.cadence !== saved.summary.cadence) summary.cadence = draft.summary.cadence;
    if (draft.summary.time !== saved.summary.time) summary.time = draft.summary.time;
    if (Object.keys(summary).length > 0) body.summary = summary;

    const thresholds: NonNullable<NotificationPreferencesPatch['thresholds']> = {};
    if (draft.thresholds.low_rating !== saved.thresholds.low_rating) {
      thresholds.low_rating = draft.thresholds.low_rating;
    }
    if (draft.thresholds.large_order_amount !== saved.thresholds.large_order_amount) {
      // The service spells "off" as a flag rather than a null amount.
      if (draft.thresholds.large_order_amount == null) {
        thresholds.clear_large_order_amount = true;
      } else {
        thresholds.large_order_amount = draft.thresholds.large_order_amount;
      }
    }
    if (Object.keys(thresholds).length > 0) body.thresholds = thresholds;

    if (!sameSet(draft.muted_restaurants ?? [], saved.muted_restaurants ?? [])) {
      body.muted_restaurants = [...(draft.muted_restaurants ?? [])];
    }
    return body;
  }
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/** The service's reason when it gave one, nothing when it only said "error". */
function reason(err: HttpErrorResponse): string | null {
  const code = err.error?.error?.code ?? err.error?.data?.detail?.code;
  switch (code) {
    case 'category_locked':
      return 'That category is always on for this channel.';
    case 'channel_not_allowed':
      return 'That channel is not available for this category.';
    case 'category_not_available':
      return 'That category is not available for your role.';
    default: {
      const message = err.error?.error?.message ?? err.error?.message;
      return typeof message === 'string' && message.length > 0 ? message : null;
    }
  }
}

/** `4155550148` → `+1 (415) 555-0148`; anything else is left alone. */
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `+1 (${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}
